/**
 * Truck Data Transformation Utilities
 *
 * This module provides utilities to transform NextBillionAssetLocation data
 * into a format optimized for DeckGL IconLayer rendering.
 *
 * The transformation separates the heavy React component creation (popovers)
 * from the lightweight data objects needed for GPU rendering, which is key
 * to achieving high performance with thousands of markers.
 */

import { Equipment_Read_Nested } from '@treadinc/horizon-api-spec';

import { NextBillionAssetLocation } from '~hooks/useNextBillionAssetLocationHistories/models';
import { Nullable } from '~types/Nullable';

import { loadStateToPhase } from '../loadPhase';
import { TruckIconType } from '../truckIconAtlas';

/**
 * Maximum length for equipment labels on the map.
 * Longer names are truncated with ellipsis to prevent visual clutter.
 */
const MAX_LABEL_LENGTH = 10;

/**
 * Maximum length for vendor name in multi-line labels.
 */
const MAX_VENDOR_NAME_LENGTH = 10;

/**
 * Data structure for a single truck marker in the DeckGL layer.
 * This is a plain object (not a React component) for efficient GPU rendering.
 */
export interface TruckLayerDatum {
  /** Unique identifier for the truck (equipment ID or location ID) */
  id: string;
  /** Position as [longitude, latitude] for DeckGL */
  position: [number, number];
  /** Bearing in degrees (0-360) for icon rotation */
  bearing: number;
  /** Icon type to render from the atlas */
  icon: TruckIconType;
  /** Label text to display below the truck icon */
  label: string;
  /**
   * Full truck location data for popover rendering on-demand.
   * This data is NOT rendered immediately - only when user interacts.
   */
  truckLocation: NextBillionAssetLocation;
  /** Whether this truck has radii (is a moving site) */
  isMovingSite: boolean;
  /** Radii in meters if this is a moving site */
  radii: number[] | null;
}

/**
 * Options for transforming truck location data.
 */
interface TransformTruckDataOptions {
  /** Filter to only trucks assigned to this order */
  orderId?: string;
  /** Filter to only trucks assigned to this job */
  jobId?: string;
  /** Whether to include vendor name in labels */
  showVendorName: boolean;
  /** Currently active/selected marker ID (for styling) */
  activeMarkerId?: string | null;
  /** Whether to color trucks by the site they are headed toward (opt-in) */
  colorByDestination?: boolean;
}

/**
 * Truncates text with ellipsis if it exceeds the maximum length.
 *
 * @param text - The text to truncate
 * @param maxLength - Maximum allowed length before truncation
 * @returns Truncated text with ellipsis or original text if within limit
 */
const truncateWithEllipsis = (text: string, maxLength: number): string => {
  return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
};

/**
 * Generates the display label for a truck marker.
 *
 * @param equipment - The equipment data (may be null)
 * @param truckLocation - The full truck location data
 * @param showVendorName - Whether to include vendor name on a second line
 * @returns Formatted label string (may include newline for multi-line display)
 */
export const getEquipmentLabel = (
  equipment: Nullable<Equipment_Read_Nested>,
  truckLocation: NextBillionAssetLocation,
  showVendorName: boolean = false,
): string => {
  const truckIdentifier = equipment
    ? (equipment.company_share?.external_id ?? equipment.name)
    : '';

  const vendorName = truckLocation.vendorName ?? '';

  if (showVendorName && vendorName) {
    return `${truncateWithEllipsis(truckIdentifier, MAX_LABEL_LENGTH)}\n${truncateWithEllipsis(vendorName, MAX_VENDOR_NAME_LENGTH)}`;
  }

  return truncateWithEllipsis(truckIdentifier, MAX_LABEL_LENGTH);
};

/**
 * Validates that a truck location has valid coordinates.
 *
 * @param truckLocation - The truck location to validate
 * @returns True if the location has valid finite lat/lng coordinates
 */
export const isValidTruckLocation = (
  truckLocation: NextBillionAssetLocation,
): boolean => {
  const lat = Number.parseFloat(truckLocation.lat);
  const lng = Number.parseFloat(truckLocation.lon);
  return Number.isFinite(lat) && Number.isFinite(lng);
};

/**
 * Checks if a truck should be filtered based on order/job criteria.
 *
 * @param truckLocation - The truck location to check
 * @param options - Filter options containing orderId and jobId
 * @returns True if the truck should be included (passes filters)
 */
const shouldIncludeTruck = (
  truckLocation: NextBillionAssetLocation,
  options: TransformTruckDataOptions,
): boolean => {
  // If order filter is set, only include trucks assigned to that order
  if (options.orderId && truckLocation.orderId !== options.orderId) {
    return false;
  }

  // If job filter is set, only include trucks assigned to that job
  if (options.jobId && truckLocation.jobId !== options.jobId) {
    return false;
  }

  return true;
};

/**
 * Determines the icon type for a truck based on its state.
 *
 * @param truckLocation - The truck location data
 * @param isActive - Whether this truck is currently active/selected
 * @returns The icon type to use from the atlas
 */
const getTruckIconType = (
  truckLocation: NextBillionAssetLocation,
  isActive: boolean,
  colorByDestination: boolean,
): TruckIconType => {
  // Moving sites use a different icon (circle)
  if (truckLocation.radii?.length) {
    return 'moving_site';
  }

  // Active/selected trucks use a highlighted icon
  if (isActive) {
    return 'truck_active';
  }

  // Idle trucks use an orange icon
  if (truckLocation.idle) {
    return 'truck_idle';
  }

  // When the "color trucks by destination" option is on, trucks on an active
  // load leg are colored to match the site they are headed toward (pickup site
  // = blue, dropoff site = navy) so a dispatcher can see each truck's
  // destination at a glance.
  if (colorByDestination) {
    const loadPhase = loadStateToPhase(truckLocation.loadState);
    if (loadPhase === 'pickup') {
      return 'truck_pickup';
    }
    if (loadPhase === 'dropoff') {
      return 'truck_dropoff';
    }
  }

  return 'truck';
};

/**
 * Transforms a single truck location into a DeckGL layer datum.
 *
 * @param truckLocation - The source truck location data
 * @param options - Transform options including vendor name display
 * @returns A TruckLayerDatum ready for DeckGL rendering
 */
const transformSingleTruck = (
  truckLocation: NextBillionAssetLocation,
  options: TransformTruckDataOptions,
): TruckLayerDatum => {
  const lat = Number.parseFloat(truckLocation.lat);
  const lng = Number.parseFloat(truckLocation.lon);
  const id = truckLocation.equipment?.id ?? truckLocation.id;
  const isActive = options.activeMarkerId === id;
  const hasRadii = Boolean(truckLocation.radii?.length);

  return {
    id,
    position: [lng, lat], // DeckGL uses [lng, lat] order
    bearing: truckLocation.bearing ? Math.round(Number(truckLocation.bearing)) : 0,
    icon: getTruckIconType(truckLocation, isActive, options.colorByDestination ?? false),
    label: getEquipmentLabel(
      truckLocation.equipment,
      truckLocation,
      options.showVendorName,
    ),
    truckLocation,
    isMovingSite: hasRadii,
    radii: hasRadii ? truckLocation.radii!.map((r) => r.radiusMeters) : null,
  };
};

/**
 * Transforms a collection of truck locations into DeckGL layer data.
 *
 * This is the main entry point for converting NextBillionAssetLocation objects
 * into the optimized format needed for GPU-accelerated rendering.
 *
 * Key optimizations:
 * - Filters invalid coordinates early (before any processing)
 * - Applies order/job filters before transformation
 * - Creates plain objects (not React components) for GPU efficiency
 * - Stores full data reference for on-demand popover rendering
 *
 * @param truckLocationsById - Map of truck locations keyed by equipment/device ID
 * @param options - Transform options for filtering and display
 * @returns Array of TruckLayerDatum objects ready for DeckGL
 */
export const transformTruckLocations = (
  truckLocationsById: Record<string, NextBillionAssetLocation>,
  options: TransformTruckDataOptions,
): TruckLayerDatum[] => {
  return Object.values(truckLocationsById)
    .filter((truckLocation) => {
      // Skip invalid coordinates
      if (!isValidTruckLocation(truckLocation)) {
        return false;
      }

      // Apply order/job filters
      if (!shouldIncludeTruck(truckLocation, options)) {
        return false;
      }

      return true;
    })
    .map((truckLocation) => transformSingleTruck(truckLocation, options));
};

/**
 * Separates truck data into regular trucks and moving sites.
 *
 * Moving sites require special rendering (circles around them),
 * so they're separated for different layer handling.
 *
 * @param truckData - Array of transformed truck data
 * @returns Object containing separate arrays for trucks and moving sites
 */
export const separateTruckAndMovingSites = (
  truckData: TruckLayerDatum[],
): {
  trucks: TruckLayerDatum[];
  movingSites: TruckLayerDatum[];
} => {
  const trucks: TruckLayerDatum[] = [];
  const movingSites: TruckLayerDatum[] = [];

  for (const datum of truckData) {
    if (datum.isMovingSite) {
      movingSites.push(datum);
    } else {
      trucks.push(datum);
    }
  }

  return { trucks, movingSites };
};
